import {
  Box,
  Button,
  styled,
  Typography,
  Pagination,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CustomInput, CustomSelect, PageTitle } from "../components-new/common";
import { DeleteIcon, PlusIcon } from "../components-new/icons";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
  useCreateSuiteQuery,
  useModuleQuery,
  useProductQuery,
  useSearchInAllTestCase,
  useSearchInQuery,
  useSearchSuiteQuery,
  useTestCaseQuery,
  useTestSuiteQueryNew,
  useTestCasePaginationQuery,
  useFetchTestCasesInSuite,
  useDeletedTestCasePaginationQuery,
  useCheckSuiteNameQuery,
} from "../useQuery";
import TestSuiteItems from "./TestSuite/TestSuiteItems";
import TestSuiteCases from "./TestSuite/TestSuiteCases";
import { transformPaginatedTestCases } from "./TestSuite/suiteUtils";
import { useDispatch } from "react-redux";
import { updateSuiteList } from "../Redux/TestSuite/TestSuite";
import CustomDialog from "../components-new/common/CustomDialog";
import { useQueryClient } from "@tanstack/react-query";
import { toNumber } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { HelpIconButton } from "../components-new/help/help-modal";
import { useNotification, useDebouncedValue, useModuleReadOnly } from "../hooks";

const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
`;

const StyledBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e2e6f3;
  border-radius: 8px;
  cursor: pointer;
  padding: 9px 20px;
`;

const TestSuite = () => {
  const itemsPerRow = 4;
  const itemsPerPage = 52;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotification();
  const path = location.pathname;
  const queryClient = useQueryClient();

  const [productId, setProductId] = useState(null);
  const [moduleId, setModuleId] = useState(null);
  // const [testCaseId, setTestCaseId] = useState(null);
  const [showTestCases, setShowTestCases] = useState(false);
  const [showDeletedTestCases, setShowDeletedTestCases] = useState(false);
  const [showDeletedSuites, setShowDeletedSuites] = useState(false);
  const [suiteId, setSuiteId] = useState(null);
  const [prodName, setProdName] = useState(null);
  const [moduleName, setModuleName] = useState(null);
  const [titleSuiteName, setTitleSuiteName] = useState(null);
  const [isCreateModal, setIsCreateModal] = useState(false);
  const [suiteName, setSuiteName] = useState("");
  const [newSuiteName, setNewSuiteName] = useState("");
  const [suiteData, setSuiteData] = useState([]);
  const [isSearched, setIsSearched] = useState(false);
  const [suiteTestCasesPage, setSuiteTestCasesPage] = useState(1);
  const debouncedSuiteName = useDebouncedValue(newSuiteName, 300);
  const { data: suiteNameCheckData, isLoading: isCheckingSuiteName } = useCheckSuiteNameQuery({
    testSuite: debouncedSuiteName.trim(),
    prodId: productId,
  });
  const suiteNameExists = debouncedSuiteName.trim() ? !!suiteNameCheckData?.exists : null;

  const { data: products } = useProductQuery();

  // Auto-select first product (skip if returning via browser back)
  useEffect(() => {
    const stateProductId = location.state?.productId || location.state?.prodId;
    if (stateProductId) return;

    if (!productId && products && products.length > 0) {
      const firstProduct = products[0];
      setProductId(firstProduct.idproducts);
      setProdName(firstProduct.productname);
      navigate(location.pathname, {
        replace: true,
        state: { productId: firstProduct.idproducts, productName: firstProduct.productname },
      });
    }
  }, [productId, products, location.state, navigate, location.pathname]);

  const { refetchWithParams: createTestSuite, data: createSuiteData } = useCreateSuiteQuery({
    onSuccess: () => {
      notify(`Test suite ${newSuiteName} created successfully!`, "success");
    },
    onError: (error) => {
      notify(`${error}`, "error");
    },
  });

  const {
    data: testCasesinSuite,
    refetch: refetchTestCasesinSuite,
    isLoading: isLoadingTestCasesinSuite,
  } = useFetchTestCasesInSuite(suiteId, suiteTestCasesPage);

  const {
    data,
    refetch,
    isSuccess,
    isLoading: isLoadingSuites,
  } = useTestSuiteQueryNew(productId, !!productId, showDeletedSuites);
  const {
    refetchWithParams: fetchSuiteSearch,
    data: searchSuiteData,
    isLoading: isSearchingSuites,
  } = useSearchSuiteQuery();
  const {
    refetchWithParams: fetchSearchInSuite,
    data: searchInSuiteData,
    isLoading: isSearchingInSuite,
  } = useSearchInQuery();
  const {
    refetchWithParams: fetchSearchAllTC,
    data: searchTCData,
    isLoading: isSearchingTC,
  } = useSearchInAllTestCase();
  const { data: modules, refetch: refetchModules } = useModuleQuery(productId, {
    enabled: false,
  });

  const isReadOnly = useModuleReadOnly(moduleId, modules);
  const { data: testCases, refetch: refetchTestCases } = useTestCaseQuery(moduleId, "", {
    enabled: false,
  });

  const [currentPage, setCurrentPage] = useState(1);

  const { data: activeTestCaseData, isLoading: isLoadingActiveCases } = useTestCasePaginationQuery(
    moduleId,
    currentPage,
    {
      enabled: !!moduleId && !showDeletedTestCases,
    },
  );
  const { data: deletedTestCaseData, isLoading: isLoadingDeletedCases } =
    useDeletedTestCasePaginationQuery(moduleId, currentPage, {
      enabled: !!moduleId && showDeletedTestCases,
    });

  const testCaseData = showDeletedTestCases ? deletedTestCaseData : activeTestCaseData;

  const totalPages = testCaseData?.[0]?.totalPages || 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [moduleId]);

  useEffect(() => {
    setSuiteTestCasesPage(1);
  }, [suiteId]);

  useEffect(() => {
    if (location.state) {
      const incomingProductId = location.state.productId || location.state.prodId;
      const incomingProductName = location.state.productName;

      if (incomingProductId) {
        setProductId(incomingProductId);
        if (incomingProductName) {
          setProdName(incomingProductName);
        } else if (products?.length > 0) {
          const product = products.find((p) => p.idproducts === incomingProductId);
          if (product) setProdName(product.productname);
        }
      }

      if (location.state.suiteId) {
        setSuiteId(location.state.suiteId);
        setShowTestCases(true);
        setShowDeletedSuites(false);
      }
    }
  }, [location.state, products]);

  useEffect(() => {
    if (!createSuiteData) return;

    queryClient.invalidateQueries(["testSuiteNew"]);
  }, [createSuiteData, queryClient]);

  useEffect(() => {
    setSuiteData(searchSuiteData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSuiteData]);

  useEffect(() => {
    if (isSuccess && data) {
      setSuiteData(data);
      if (!showDeletedSuites) {
        dispatch(updateSuiteList(data.flat()));
      }
    }
  }, [isSuccess, data, dispatch, showDeletedSuites]);

  useEffect(() => {
    if (!productId) return;

    refetch();
    refetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    if (!moduleId) return;
    refetchTestCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const handleSuiteClick = (item) => {
    const foundSuite = suiteData.flat().find((suite) => suite.idtest_suite === item);
    const suiteName = foundSuite?.Test_Suite || "Suite";
    const params = new URLSearchParams({
      productId: productId || "",
      productName: prodName || "",
      suiteName: suiteName,
    });
    navigate(`/new/test-suites/suite/${item}?${params.toString()}`);
  };

  const handleNestedSuiteClick = (nestedSuiteId, nestedSuiteName) => {
    const params = new URLSearchParams({
      productId: productId || "",
      productName: prodName || "",
      suiteName: nestedSuiteName || "Suite",
      parentSuiteId: suiteId || "",
      parentSuiteName: titleSuiteName || "",
    });
    navigate(`/new/test-suites/suite/${nestedSuiteId}?${params.toString()}`);
  };

  const handleClearSelection = () => {
    setProductId(null);
    setProdName(null);
    setModuleId(null);
    setModuleName(null);
    setSuiteId(null);
    setTitleSuiteName(null);
    setShowTestCases(false);
    setShowDeletedTestCases(false);
    setShowDeletedSuites(false);
    setIsSearched(false);
    setSuiteName("");
  };

  const CreateActions = () => {
    return (
      <>
        <Button
          sx={{ color: "#ff5336" }}
          onClick={() => {
            setIsCreateModal(false);
            setNewSuiteName("");
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ background: "#ff5336" }}
          disabled={
            !newSuiteName || suiteNameExists || isCheckingSuiteName || suiteNameExists === null
          }
          onClick={() => {
            createTestSuite(productId, newSuiteName);
            setNewSuiteName("");
            setIsCreateModal(false);
          }}
        >
          Create
        </Button>
      </>
    );
  };

  const handleSuiteSearch = () => {
    if (showDeletedSuites) return;

    fetchSuiteSearch({
      pnum: 1,
      key: suiteName,
      prodid: productId,
    });
  };

  const handleSearchInSuite = () => {
    fetchSearchInSuite({
      pnum: 1,
      key: suiteName,
      suiteid: suiteId,
    });
  };

  const handleSearchAllTC = () => {
    fetchSearchAllTC({
      pnum: 1,
      key: suiteName,
      modid: moduleId,
    });
  };

  const handleClearSearch = () => {
    setSuiteName("");
    setIsSearched(false);

    if (!showTestCases && data) {
      setSuiteData(data);
    }
  };

  const getSearchContextLabel = () => {
    if (!showTestCases && showDeletedSuites) return "Search disabled in deleted suites view";
    if (!showTestCases) return "Search Suites";
    if (suiteId) return `Search in "${titleSuiteName || "Suite"}"`;
    if (moduleId) return `Search in "${moduleName || "Module"}"`;
    return "Search";
  };

  const handleDeleteSuccessInSearch = () => {
    setIsSearched(false);
    setSuiteName("");
  };

  const handleName = (e) => {
    const value = e.target.value;
    const regex = /^[A-Za-z0-9 _-]*$/;
    if (value.length <= 250 && regex.test(value)) {
      setNewSuiteName(value);
    }
  };

  return (
    <>
      <CustomDialog
        title={"Are you sure you want to create test suite?"}
        open={isCreateModal}
        actions={<CreateActions />}
        content={
          <>
            <CustomInput
              labelinput="Suite Name"
              placeholder={"Enter Suite Name"}
              value={newSuiteName}
              // onChange={(e) => setNewSuiteName(e.target.value)}
              onChange={handleName}
              name={"createSuiteModal"}
            />
            {newSuiteName && (
              <>
                {isCheckingSuiteName && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                    <CircularProgress size={12} />
                    <Typography variant="caption" sx={{ color: "#888" }}>
                      Checking availability...
                    </Typography>
                  </Box>
                )}
                {!isCheckingSuiteName && suiteNameExists === true && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}
                  >
                    A test suite with this name already exists in the selected product.
                  </Typography>
                )}
                {!isCheckingSuiteName && suiteNameExists === false && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#2e7d32", mt: 0.5, display: "block" }}
                  >
                    Test suite name is available.
                  </Typography>
                )}
              </>
            )}
          </>
        }
      />
      <Box sx={{ mt: 2, mb: 4 }} display="flex" alignItems="center" justifyContent="space-between">
        {/* Left */}
        <PageTitle title="Test Suite" />

        {/* Right – grouped actions */}
        {/* 2 Scenarios:
      1. product only (no module): Show single toggle button
      2. product + module: Show two separate buttons for active/deleted test cases */}

        {/* Scenario 1: Only product selected (no module) - Single toggle button */}
        {productId && !moduleId && (
          <Box display="flex" alignItems="center" gap={3}>
            <Box display="flex" alignItems="center">
              <HelpIconButton path={path} />
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              {!showTestCases ? (
                // show "View All Test Cases" when in suite view (only enabled if module is selected)
                <Tooltip
                  title={
                    moduleId
                      ? "View all test cases in the selected module"
                      : "Please select a module first"
                  }
                  placement="top"
                  arrow
                >
                  <span>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        if (moduleId) {
                          setShowTestCases(true);
                          setTitleSuiteName(null);
                          setSuiteId(null);
                          setShowDeletedTestCases(false);
                          setShowDeletedSuites(false);
                        }
                      }}
                      sx={{
                        opacity: moduleId ? 1 : 0.5,
                        cursor: moduleId ? "pointer" : "not-allowed",
                        pointerEvents: moduleId ? "auto" : "none",
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View All Test Cases</Typography>
                    </StyledBox>
                  </span>
                </Tooltip>
              ) : (
                // show "View All Test Suites" when in test case view
                <StyledBox
                  display="flex"
                  alignItems="center"
                  onClick={() => {
                    setShowTestCases(false);
                    setTitleSuiteName(null);
                    setSuiteId(null);
                    setShowDeletedTestCases(false);
                    setShowDeletedSuites(false);
                  }}
                >
                  <FolderOutlinedIcon />
                  <Typography ml={1}>View All Test Suites</Typography>
                </StyledBox>
              )}
              {!showTestCases && (
                <Tooltip
                  title={
                    showDeletedSuites
                      ? "Switch back to active test suites"
                      : "View deleted test suites for this product"
                  }
                  arrow
                >
                  <StyledBox
                    display="flex"
                    alignItems="center"
                    onClick={() => {
                      setShowDeletedSuites((prev) => !prev);
                      setIsSearched(false);
                      setSuiteName("");
                    }}
                  >
                    <FolderOutlinedIcon />
                    <Typography ml={1}>
                      {showDeletedSuites ? "View Active Test Suites" : "View Deleted Test Suites"}
                    </Typography>
                  </StyledBox>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        {/* scenarios 2: Product + Module selected - Two separate buttons */}
        {productId && moduleId && (
          <Box display="flex" alignItems="center" gap={3}>
            <Box display="flex" alignItems="center">
              <HelpIconButton path={path} />
            </Box>

            {/* RIGHT BUTTONS – three states based on showTestCases + showDeletedTestCases */}
            <Box display="flex" alignItems="center" gap={2}>
              {/* 1) TEST SUITES VIEW */}
              {!showTestCases && (
                <>
                  <Tooltip title="View all active test cases in this module" arrow>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowTestCases(true);
                        setTitleSuiteName(null);
                        setSuiteId(null);
                        setShowDeletedTestCases(false);
                        setShowDeletedSuites(false);
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View All Test Cases</Typography>
                    </StyledBox>
                  </Tooltip>

                  <Tooltip title="View deleted test cases in this module" arrow>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowTestCases(true);
                        setTitleSuiteName(null);
                        setSuiteId(null);
                        setShowDeletedTestCases(true);
                        setShowDeletedSuites(false);
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View Deleted Test Cases</Typography>
                    </StyledBox>
                  </Tooltip>

                  <Tooltip
                    title={
                      showDeletedSuites
                        ? "Switch back to active test suites"
                        : "View deleted test suites for this product"
                    }
                    arrow
                  >
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowDeletedSuites((prev) => !prev);
                        setShowDeletedTestCases(false);
                        setIsSearched(false);
                        setSuiteName("");
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>
                        {showDeletedSuites ? "View Active Test Suites" : "View Deleted Test Suites"}
                      </Typography>
                    </StyledBox>
                  </Tooltip>
                </>
              )}

              {/* 2) ACTIVE TEST CASES VIEW */}
              {showTestCases && !showDeletedTestCases && (
                <>
                  <Tooltip title="Go back to all test suites" arrow>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowTestCases(false);
                        setTitleSuiteName(null);
                        setSuiteId(null);
                        setShowDeletedTestCases(false);
                        setShowDeletedSuites(false);
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View All Test Suites</Typography>
                    </StyledBox>
                  </Tooltip>

                  <Tooltip title="View deleted test cases in this module" arrow>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowTestCases(true);
                        setTitleSuiteName(null);
                        setSuiteId(null);
                        setShowDeletedTestCases(true);
                        setShowDeletedSuites(false);
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View Deleted Test Cases</Typography>
                    </StyledBox>
                  </Tooltip>
                </>
              )}

              {/* 3) DELETED TEST CASES VIEW */}
              {showTestCases && showDeletedTestCases && (
                <>
                  <Tooltip title="Go back to all test suites" arrow>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowTestCases(false);
                        setTitleSuiteName(null);
                        setSuiteId(null);
                        setShowDeletedTestCases(false);
                        setShowDeletedSuites(false);
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View All Test Suites</Typography>
                    </StyledBox>
                  </Tooltip>

                  <Tooltip title="View all active test cases in this module" arrow>
                    <StyledBox
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        setShowTestCases(true);
                        setTitleSuiteName(null);
                        setSuiteId(null);
                        setShowDeletedTestCases(false);
                        setShowDeletedSuites(false);
                      }}
                    >
                      <FolderOutlinedIcon />
                      <Typography ml={1}>View All Test Cases</Typography>
                    </StyledBox>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" columnGap={2} rowGap={2}>
          <CustomSelect
            label={"Application"}
            options={(Array.isArray(products) ? products : []).map((prodItem) => ({
              label: prodItem.productname,
              value: prodItem.idproducts,
            }))}
            width={"230px"}
            background={"#ffffff"}
            value={productId || ""}
            onChange={(e) => {
              const newProductId = e.target.value;
              const product = (products ?? []).find((item) => item.idproducts === newProductId);
              const newProductName = product?.productname || "";

              setProdName(newProductName);
              setProductId(newProductId);
              setModuleId(null);
              setModuleName(null);
              setSuiteId(null);
              setTitleSuiteName(null);
              setShowTestCases(false);
              setShowDeletedTestCases(false);
              setShowDeletedSuites(false);
              setIsSearched(false);
              setSuiteName("");

              navigate(location.pathname, {
                replace: true,
                state: { productId: newProductId, productName: newProductName },
              });
            }}
          />
          {productId && (
            <CustomSelect
              label={"Module"}
              options={(Array.isArray(modules) ? modules : []).map((modItem) => ({
                label: modItem.modulename,
                value: modItem.idmodules,
              }))}
              width={"230px"}
              background={"#ffffff"}
              value={moduleId || ""}
              onChange={(e) => {
                const newModuleId = e.target.value;
                const selectedModule = (modules ?? []).find((m) => m.idmodules === newModuleId);

                setModuleId(newModuleId);
                setModuleName(selectedModule?.modulename || "");

                // We’re not looking at a specific suite any more
                setSuiteId(null);
                setTitleSuiteName(null);

                // Don't automatically show test cases - user must click "View All Test Cases" button
                setShowTestCases(false);
                setShowDeletedTestCases(false);
                setShowDeletedSuites(false);
                // Optional: reset search inside module
                setIsSearched(false);
                setSuiteName("");
              }}
            />
          )}
          <Tooltip title="Clear all filters and selections" arrow>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              size="medium"
              onClick={handleClearSelection}
              sx={{
                color: "#828286",
                background: "#ffffff",
                height: "55px",
                border: "1px solid #E2E6F3",
                outline: 0,
                textTransform: "capitalize",
                mt: 2.5,
              }}
            >
              Clear filter
            </Button>
          </Tooltip>
        </Box>
        <Box display="flex" alignItems="flex-end" columnGap={2}>
          <CustomInput
            labelinput={getSearchContextLabel()}
            placeholder={showTestCases ? "Search test case" : "Search test suite"}
            value={suiteName}
            onChange={(e) => setSuiteName(e.target.value)}
            name={"searchInput"}
            disabled={!showTestCases && showDeletedSuites}
          />
          <Tooltip title={getSearchContextLabel()} arrow>
            <span>
              <StyledButton
                disabled={
                  !productId ||
                  isSearchingSuites ||
                  isSearchingInSuite ||
                  isSearchingTC ||
                  (!showTestCases && showDeletedSuites)
                }
                variant="contained"
                onClick={() => {
                  if (!showTestCases && showDeletedSuites) return;
                  suiteName ? setIsSearched(true) : setIsSearched(false);
                  if (!showTestCases) handleSuiteSearch();
                  if (showTestCases && suiteId) handleSearchInSuite();
                  if (showTestCases && moduleId) handleSearchAllTC();
                }}
                startIcon={
                  isSearchingSuites || isSearchingInSuite || isSearchingTC ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isSearchingSuites || isSearchingInSuite || isSearchingTC
                  ? "Searching..."
                  : "Search"}
              </StyledButton>
            </span>
          </Tooltip>
          {isSearched && suiteName && (
            <Button
              variant="outlined"
              size="medium"
              onClick={handleClearSearch}
              sx={{
                height: "55px",
                textTransform: "capitalize",
                borderColor: "#5541D7",
                color: "#5541D7",
                whiteSpace: "nowrap",
              }}
            >
              Clear: "{suiteName}"
            </Button>
          )}
          <Tooltip title="Create a new test suite" arrow>
            <span>
              <StyledButton
                variant="contained"
                color="warning"
                onClick={() => setIsCreateModal(true)}
                endIcon={<PlusIcon />}
                sx={{
                  backgroundColor: "#ff5336",
                  boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
                }}
                disabled={!productId || showDeletedSuites}
              >
                Create Suite
              </StyledButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {!showTestCases &&
        productId &&
        (isLoadingSuites ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <TestSuiteItems
            data={suiteData}
            itemsPerPage={itemsPerPage}
            itemsPerRow={itemsPerRow}
            showTestCases={showTestCases}
            handleSuiteClick={handleSuiteClick}
            prodName={prodName}
            selectedProdId={productId}
            showDeletedSuites={showDeletedSuites}
            isReadOnly={isReadOnly}
          />
        ))}

      {showTestCases && !isSearched && (
        <>
          {(suiteId ? isLoadingTestCasesinSuite : isLoadingActiveCases || isLoadingDeletedCases) ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TestSuiteCases
                data={suiteId ? transformPaginatedTestCases(testCasesinSuite) : testCaseData}
                itemsPerRow={itemsPerRow}
                showTestCases={showTestCases}
                testSuiteId={suiteId}
                setShowTestCases={setShowTestCases}
                selectedProdId={productId}
                titleSuiteName={titleSuiteName}
                selectedModule={moduleName}
                showDeletedTestCases={showDeletedTestCases}
                handleNestedSuiteClick={handleNestedSuiteClick}
                isSearched={false}
                isReadOnly={isReadOnly}
              />
              {suiteId && (
                <Box py={2} display="flex" justifyContent="center">
                  <Pagination
                    count={testCasesinSuite?.[0]?.list?.[0]?.totalPages || 1}
                    page={suiteTestCasesPage}
                    onChange={(e, newpage) => setSuiteTestCasesPage(newpage)}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              )}
              {!suiteId && moduleId && (
                <Box py={2} display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, newpage) => setCurrentPage(newpage)}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
      {showTestCases && isSearched && (
        <TestSuiteCases
          data={!moduleId ? searchInSuiteData : searchTCData}
          itemsPerRow={itemsPerRow}
          showTestCases={showTestCases}
          testSuiteId={suiteId}
          setShowTestCases={setShowTestCases}
          selectedProdId={productId}
          selectedModule={moduleName}
          showDeletedTestCases={showDeletedTestCases}
          handleNestedSuiteClick={handleNestedSuiteClick}
          isSearched={true}
          onDeleteSuccess={handleDeleteSuccessInSearch}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
};

export default TestSuite;
